import type { FundOwnershipDetailModel } from 'api';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { WaterfallFundSummaryRowConfigProps } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/waterfallFundSummaryRowConfig.props';
import { AggregatedFundOwnershipDetail } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig.types';
import {
  WaterfallFundSummaryColumn,
  WaterfallFundSummaryTableData,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig.types';
import { toNumberOrZero } from 'utilities/toNumberOrZero/toNumberOrZero';

// Builds row config with a generic title row, aggregated data rows, and a totals row
export const WaterfallFundSummaryRowConfig = (
  props: WaterfallFundSummaryRowConfigProps
): ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>[] => {
  const { captable, historicalInvestedCapitalTotals } = props;
  const details = captable.fund_ownership?.fund_ownership_detail ?? [];
  const relatedEntityFodMap = details
    .filter(fod => fod.related_entity)
    .reduce(
      (acc, fod) => {
        if (acc[fod.related_entity as number]) {
          acc[fod.related_entity as number].push(fod);
        } else {
          acc[fod.related_entity as number] = [fod];
        }
        return acc;
      },
      {} as { [key: number]: FundOwnershipDetailModel[] }
    );

  const map = new Map<string, AggregatedFundOwnershipDetail>();

  details.forEach(detail => {
    // Skip synthetic CFL-only FODs (id === null). Historical totals below
    // replace or create each owner's invested capital and realized proceeds,
    // so counting synthetic rows here would double-count CFL-only activity.
    // Real FODs always carry an integer id, so this is a clean discriminator.
    if (detail.id == null) {
      return;
    }
    // Fund Summary reports firm funds only. Related-entity owners remain
    // represented by their dedicated Waterfall tables.
    const idNum = detail.fund;
    if (idNum == null) {
      return;
    }
    const key = `fund_${idNum}`;

    const invested = toNumberOrZero(detail.invested_capital);
    const cash = toNumberOrZero(detail.cash_distribution);
    const shares = toNumberOrZero(detail.shares_distribution);
    const realized = cash + shares;
    const loanVal = toNumberOrZero(detail.loan_value);

    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        id: idNum,
        fund: idNum,
        related_entity: null,
        invested_capital: invested.toString(),
        cash_distribution: cash.toString(),
        shares_distribution: shares.toString(),
        realized_value: realized.toString(),
        loan_value: loanVal.toString(),
        related_reference_set:
          detail.entity_reference != null && relatedEntityFodMap[detail.entity_reference as unknown as number]
            ? [detail.entity_reference as unknown as number]
            : [],
        // IRR is applied via useWaterfallIrr onChange — do not bake values into row
        // config or the summary sheet rebuilds on every IRR write and loops.
        irr: 0,
      } as AggregatedFundOwnershipDetail);
    } else {
      const nextInvested = toNumberOrZero(existing.invested_capital) + invested;
      const nextCash = toNumberOrZero(existing.cash_distribution) + cash;
      const nextShares = toNumberOrZero(existing.shares_distribution) + shares;
      existing.invested_capital = nextInvested.toString();
      existing.cash_distribution = nextCash.toString();
      existing.shares_distribution = nextShares.toString();
      existing.realized_value = (nextCash + nextShares).toString();
      existing.loan_value = (toNumberOrZero(existing.loan_value) + loanVal).toString();
      // Merge entity_reference into related_reference_set when it belongs to this related-entity group.
      const currentSet = existing.related_reference_set ?? [];
      const ref = detail.entity_reference as unknown as number | null;
      const incoming = ref != null && relatedEntityFodMap[ref] ? [ref] : [];
      if (incoming.length) {
        const merged = new Set<number>([...currentSet, ...incoming]);
        existing.related_reference_set = Array.from(merged);
      }
    }
  });

  // Historical totals already use transaction-date FX to reach the selected
  // output currency. Keep both inputs to MOIC in that domain; Exit Date FX must
  // not revalue either Invested Capital or Previous Proceeds.
  historicalInvestedCapitalTotals.forEach(total => {
    if (total.owner_type !== 'fund') {
      return;
    }
    const key = `fund_${total.owner_id}`;
    const investedCapital = toNumberOrZero(total.invested_capital).toString();
    const realizedValue = toNumberOrZero(total.realized_value).toString();
    const existing = map.get(key);
    if (existing) {
      existing.invested_capital = investedCapital;
      existing.realized_value = realizedValue;
      return;
    }

    map.set(key, {
      id: total.owner_id,
      fund: total.owner_id,
      related_entity: null,
      invested_capital: investedCapital,
      cash_distribution: '0',
      shares_distribution: '0',
      realized_value: realizedValue,
      loan_value: '0',
      related_reference_set: [],
      irr: 0,
      isCflOnlyFund: true,
    } as AggregatedFundOwnershipDetail);
  });

  const aggregated: AggregatedFundOwnershipDetail[] = Array.from(map.values());

  // Build rows: header placeholder + data + totals placeholder
  const rows: ICell<
    AggregatedFundOwnershipDetail,
    object,
    WaterfallFundSummaryColumn,
    WaterfallFundSummaryTableData
  >[] = [];

  // Title/header placeholder row (parser will render column headers)
  rows.push(
    {} as ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>
  );

  // Data rows
  rows.push(
    ...aggregated.map(
      data =>
        ({ data }) as ICell<
          AggregatedFundOwnershipDetail,
          object,
          WaterfallFundSummaryColumn,
          WaterfallFundSummaryTableData
        >
    )
  );

  // Totals placeholder row (parser will compute totals)
  rows.push(
    {} as ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>
  );

  return rows;
};
